/**
 * lib/mapbox.ts
 *
 * Pure Mapbox API functions — no React, no hooks, no side-effects.
 * Each function reads NEXT_PUBLIC_MAPBOX_TOKEN internally so callers
 * never have to pass it themselves.
 *
 * Usage (anywhere in the app):
 *   import { geocodeAddress, suggestAddresses } from "~/lib/mapbox";
 *   const coords = await geocodeAddress("1234 Market St, SF, CA 94102");
 */

import { env } from "~/env";
import { toStateCode } from "./us-states";

// ---------------------------------------------------------------------------
// Shared types — exported so callers can use them for state typing
// ---------------------------------------------------------------------------

export type MapboxCoords = {
  lat: number;
  lng: number;
};

export type AddressFields = {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
};

/** A suggestion item returned by the Address Autofill /suggest endpoint. */
export type MapboxSuggestion = {
  mapbox_id: string;
  feature_name: string;
  description?: string;
  full_address?: string;
  action: { id: string };
};

/** A city/place suggestion (same shape, typed separately for clarity). */
export type CitySuggestion = {
  mapbox_id: string;
  name: string;
  place_formatted?: string;
};

/** The feature returned by /retrieve after selecting a suggestion. */
export type RetrievedFeature = {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
};

/**
 * A suggestion item covering addresses, cities/neighborhoods, and POIs.
 * Used by `suggestPlaces` for general location search (e.g. a search bar).
 */
export type PlaceSuggestion = {
  mapbox_id: string;
  /** Primary display name (street address, place name, or POI name). */
  name: string;
  /** Secondary line shown in the dropdown (city, state, etc.). */
  place_formatted?: string;
  feature_type?: string;
};

/**
 * Resolved result after the user selects a `PlaceSuggestion`.
 * Always has coords; structured fields are best-effort (empty for pure POIs).
 */
export type RetrievedPlace = {
  /** Human-readable label to show in the search input after selection. */
  label: string;
  /**
   * POI / place / neighborhood name (e.g. "Starbucks"). `null` for plain
   * addresses where the name would just restate the street.
   */
  name: string | null;
  lat: number;
  lng: number;
  city: string;
  state: string;
};

// ---------------------------------------------------------------------------
// Internal shared types
// ---------------------------------------------------------------------------

type MapboxContext = {
  address?: { address_number?: string; street_name?: string };
  street?: { name?: string };
  postcode?: { name?: string };
  place?: { name?: string };
  region?: { region_code?: string; name?: string };
};

type RetrieveApiFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    name?: string;
    place_name?: string;
    full_address?: string;
    feature_type?: string;
    context?: MapboxContext;
  };
};

type AutofillRetrieveFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    address_line1?: string;
    address_level1?: string;
    address_level2?: string;
    postcode?: string;
  };
};

type ReverseApiFeature = {
  properties?: {
    context?: MapboxContext;
    full_address?: string;
  };
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getToken(): string {
  const token = env.NEXT_PUBLIC_MAPBOX_TOKEN;
  if (!token) {
    console.warn("[mapbox] NEXT_PUBLIC_MAPBOX_TOKEN is not set");
  }
  return token;
}

function parseContext(ctx: MapboxContext): AddressFields {
  const addrNum = ctx.address?.address_number ?? "";
  const streetName = ctx.street?.name ?? ctx.address?.street_name ?? "";
  return {
    streetAddress: [addrNum, streetName].filter(Boolean).join(" "),
    city: ctx.place?.name ?? "",
    state: (ctx.region?.region_code ?? ctx.region?.name ?? "")
      .toUpperCase()
      .slice(0, 2),
    zip: (ctx.postcode?.name ?? "").replace(/\D/g, "").slice(0, 5),
  };
}

// ---------------------------------------------------------------------------
// Static map — build a Mapbox Static Images URL for a single coordinate
// ---------------------------------------------------------------------------

const STATIC_BASE =
  "https://api.mapbox.com/styles/v1/mapbox/streets-v12/static";

/** Marker pin color (hex without `#`) — EZSpot accent blue. */
const PIN_COLOR = "0073e9";

export type StaticMapOptions = {
  /** Map zoom level (0–22). Higher = closer. */
  zoom?: number;
  /** Rendered width in points. */
  width?: number;
  /** Rendered height in points. */
  height?: number;
  /** Retina factor — `2` renders at @2x for crisp display on hi-dpi screens. */
  retina?: 1 | 2;
};

/**
 * Builds a Mapbox Static Images URL with a single pin centered on a coordinate.
 *
 * Returns a plain image URL suitable for an `<img src>`. The caller is
 * responsible for gating visibility (e.g. the booking privacy window) before
 * requesting the image — Mapbox billing counts each render. Returns null when
 * the token is missing so callers can fall back gracefully.
 */
export function staticMapUrl(
  lat: number,
  lng: number,
  options?: StaticMapOptions,
): string | null {
  const token = getToken();
  if (!token) return null;

  const { zoom = 15, width = 600, height = 400, retina = 2 } = options ?? {};
  const overlay = `pin-l+${PIN_COLOR}(${lng},${lat})`;
  const view = `${lng},${lat},${zoom},0`;
  const size = `${width}x${height}${retina === 2 ? "@2x" : ""}`;
  return `${STATIC_BASE}/${overlay}/${view}/${size}?access_token=${token}`;
}

// ---------------------------------------------------------------------------
// 1. Forward geocode — address string → lat/lng
// ---------------------------------------------------------------------------

/**
 * Converts a full address string into coordinates.
 *
 * @example
 * const coords = await geocodeAddress("1234 Market St, San Francisco, CA 94102");
 * // { lat: 37.77, lng: -122.41 }
 */
export async function geocodeAddress(
  address: string,
): Promise<MapboxCoords | null> {
  const token = getToken();
  if (!address.trim() || !token) return null;

  const encoded = encodeURIComponent(address);
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${token}&limit=1&country=us`;

  try {
    const res = await fetch(url);
    const data = (await res.json()) as {
      features?: { center?: [number, number] }[];
    };
    const center = data.features?.[0]?.center;
    if (!center) return null;
    return { lng: center[0], lat: center[1] };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// 2. Reverse geocode — lat/lng → address fields
// ---------------------------------------------------------------------------

/**
 * Converts coordinates into structured address fields.
 * Typically called after the user drags the map pin.
 *
 * @example
 * const address = await reverseGeocode({ lat: 37.77, lng: -122.41 });
 * // { streetAddress: "1234 Market St", city: "San Francisco", state: "CA", zip: "94102" }
 */
export async function reverseGeocode(
  coords: MapboxCoords,
): Promise<AddressFields | null> {
  const token = getToken();
  if (!token) return null;

  const { lat, lng } = coords;
  const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${lng}&latitude=${lat}&access_token=${token}&country=us`;

  try {
    const res = await fetch(url);
    const data = (await res.json()) as { features?: ReverseApiFeature[] };
    const ctx = data.features?.[0]?.properties?.context;
    if (!ctx) return null;
    return parseContext(ctx);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// 3. Address autocomplete — partial street input → suggestions
// ---------------------------------------------------------------------------

/**
 * Returns address suggestions for a partial street query.
 * Debounce this on the caller side (the hook does it automatically).
 *
 * @param query       Partial address typed by the user (min 3 chars recommended)
 * @param sessionToken  UUID scoping the session for billing purposes
 *
 * @example
 * const suggestions = await suggestAddresses("1234 Mar", sessionToken);
 */
export async function suggestAddresses(
  query: string,
  sessionToken: string,
  proximity: { lat: number; lng: number } | "ip" = "ip",
): Promise<MapboxSuggestion[]> {
  const token = getToken();
  if (query.trim().length < 3 || !token) return [];

  try {
    const url = new URL(
      `https://api.mapbox.com/autofill/v1/suggest/${encodeURIComponent(query)}`,
    );
    url.searchParams.set("access_token", token);
    url.searchParams.set("session_token", sessionToken);
    url.searchParams.set("country", "us");
    url.searchParams.set("language", "en");
    url.searchParams.set("streets", "true");
    url.searchParams.set(
      "proximity",
      proximity === "ip" ? "ip" : `${proximity.lng},${proximity.lat}`,
    );

    const res = await fetch(url.toString());
    const data = (await res.json()) as { suggestions?: MapboxSuggestion[] };
    return data.suggestions ?? [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// 4. Retrieve — turn a chosen suggestion into full address + coords
// ---------------------------------------------------------------------------

/**
 * Retrieves the full details of a selected address suggestion.
 * Must be called in the same session as the originating `suggestAddresses` call.
 *
 * @example
 * const feature = await retrieveAddress(suggestion.action.id, sessionToken);
 * if (feature) {
 *   setStreetAddress(feature.streetAddress);
 *   setPinCoords({ lat: feature.lat, lng: feature.lng });
 * }
 */
export async function retrieveAddress(
  actionId: string,
  sessionToken: string,
): Promise<RetrievedFeature | null> {
  const token = getToken();
  if (!token) return null;

  try {
    const url = new URL(
      `https://api.mapbox.com/autofill/v1/retrieve/${actionId}`,
    );
    url.searchParams.set("access_token", token);
    url.searchParams.set("session_token", sessionToken);

    const res = await fetch(url.toString());
    const data = (await res.json()) as {
      features?: AutofillRetrieveFeature[];
    };
    const feature = data.features?.[0];
    if (!feature) return null;

    const props = feature.properties;
    const [lng, lat] = feature.geometry.coordinates;
    return {
      streetAddress: props.address_line1 ?? "",
      city: props.address_level2 ?? "",
      state: toStateCode(props.address_level1 ?? ""),
      zip: (props.postcode ?? "").replace(/\D/g, "").slice(0, 5),
      lat,
      lng,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// 5. City suggest — partial city name → place suggestions
// ---------------------------------------------------------------------------

/**
 * Returns city/place suggestions for a partial city name.
 * Optionally scoped to a US state code (e.g. "CA").
 *
 * @example
 * const cities = await suggestCities("San Fra", sessionToken, "CA");
 */
export async function suggestCities(
  query: string,
  sessionToken: string,
  stateFilter?: string,
): Promise<CitySuggestion[]> {
  const token = getToken();
  if (query.trim().length < 2 || !token) return [];

  try {
    const url = new URL("https://api.mapbox.com/search/searchbox/v1/suggest");
    url.searchParams.set("q", query);
    url.searchParams.set("access_token", token);
    url.searchParams.set("session_token", sessionToken);
    url.searchParams.set("country", "us");
    url.searchParams.set("types", "place");
    url.searchParams.set("limit", "6");

    if (stateFilter?.trim()) {
      url.searchParams.set("region", stateFilter.trim());
    }

    const res = await fetch(url.toString());
    const data = (await res.json()) as { suggestions?: CitySuggestion[] };
    return data.suggestions ?? [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// 6. Place suggest — addresses + cities + POIs combined (for search bars)
// ---------------------------------------------------------------------------

/**
 * Returns mixed suggestions covering addresses, neighborhoods, cities, and POIs.
 * Intended for general "where do you want to park?" search inputs.
 *
 * @example
 * const places = await suggestPlaces("Pike Place", sessionToken);
 */
export async function suggestPlaces(
  query: string,
  sessionToken: string,
  proximity: { lat: number; lng: number } | "ip" = "ip",
): Promise<PlaceSuggestion[]> {
  const token = getToken();
  if (query.trim().length < 2 || !token) return [];

  try {
    const url = new URL("https://api.mapbox.com/search/searchbox/v1/suggest");
    url.searchParams.set("q", query);
    url.searchParams.set("access_token", token);
    url.searchParams.set("session_token", sessionToken);
    url.searchParams.set("country", "us");
    url.searchParams.set("types", "address,place,neighborhood,poi");
    url.searchParams.set(
      "proximity",
      proximity === "ip" ? "ip" : `${proximity.lng},${proximity.lat}`,
    );

    const res = await fetch(url.toString());
    const data = (await res.json()) as { suggestions?: PlaceSuggestion[] };
    return data.suggestions ?? [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// 7. Retrieve place — resolve any PlaceSuggestion to coords + label
// ---------------------------------------------------------------------------

/**
 * Resolves a selected `PlaceSuggestion` into coordinates and a display label.
 * Works for all types (address, city, neighborhood, POI) — unlike
 * `retrieveAddress` which expects the address context shape.
 *
 * @example
 * const place = await retrievePlace(suggestion.mapbox_id, sessionToken);
 * if (place) {
 *   setLocation(place.label);
 *   router.push(`/search?q=${place.label}&lat=${place.lat}&lng=${place.lng}`);
 * }
 */
export async function retrievePlace(
  mapboxId: string,
  sessionToken: string,
): Promise<RetrievedPlace | null> {
  const token = getToken();
  if (!token) return null;

  try {
    const url = new URL(
      `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}`,
    );
    url.searchParams.set("access_token", token);
    url.searchParams.set("session_token", sessionToken);

    const res = await fetch(url.toString());
    const data = (await res.json()) as { features?: RetrieveApiFeature[] };
    const feature = data.features?.[0];
    if (!feature) return null;

    const [lng, lat] = feature.geometry.coordinates;
    const props = feature.properties;
    const ctx = props.context;

    // Build the best label: full_address > place_name > name
    const label = props.full_address ?? props.place_name ?? props.name ?? "";

    // Plain addresses have name === street, which is redundant; keep null for
    // those so the bookings UI doesn't restate the address as a "name".
    const name =
      props.feature_type === "address"
        ? null
        : (props.place_name ?? props.name ?? null);

    const city = ctx?.place?.name ?? "";
    const state = (ctx?.region?.region_code ?? ctx?.region?.name ?? "")
      .toUpperCase()
      .slice(0, 2);

    return { label, name, lat, lng, city, state };
  } catch {
    return null;
  }
}
